<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">

          <BackButton
              :label="$t('general.button.back')"
              :to="{ name: 'officeList' }"
              marginBottom="5"/>

          <div class="d-flex justify-content-between">
            <h6 class="element-header">
              {{ $t('office.title') }}
            </h6>
          </div>

          <div class="element-box">
            <form v-if="isLoaded" @submit.prevent="submitForm" novalidate>
              <div class="row">
                <div class="col-lg-6">
                  <BaseInput
                      v-model="formData.name"
                      label="Bezeichnung"
                      :disabled="formData.hubspotId"
                      :required=true
                      :validation="v$.name"
                      :messages="v$.name.required.$message" />
                  <BaseInput
                      v-model="formData.address"
                      label="Straße"
                      :disabled="formData.hubspotId"
                      :required=true
                      :validation="v$.address"
                      :messages="v$.address.required.$message" />
                  <div class="row">
                    <div class="col-6">
                      <BaseInput
                          v-model="formData.houseNumber"
                          label="Hausnummer"
                          :disabled="formData.hubspotId"
                          :required=true
                          :validation="v$.houseNumber"
                          :messages="v$.houseNumber.required.$message" />
                    </div>
                    <div class="col-6">
                      <BaseInput
                          v-model="formData.zip"
                          label="PLZ"
                          :disabled="formData.hubspotId"
                          :required=true
                          :validation="v$.zip"
                          :messages="v$.zip.required.$message" />
                    </div>
                  </div>
                  <div v-if="officeEngagementTypesDropdown && officeEngagementTypesDropdown.length">
                    <SelectedDropdown
                        v-model="formData.engagementType"
                        :options="officeEngagementTypesDropdown"
                        :disabled="formData.hubspotId"
                        :required="true"
                        :label="$t('general.office_engagement_type')"
                        :placeholder="$t('placeholder.engagement_type')"
                        :validation="v$.engagementType"
                        :messages="v$.engagementType.required.$message"/>
                  </div>
                  <div v-if="officeTypesDropdown && officeTypesDropdown.length">
                    <SelectedDropdown
                        v-model="formData.type"
                        :options="officeTypesDropdown"
                        :disabled="formData.hubspotId"
                        :required="true"
                        :label="$t('general.type')"
                        :placeholder="$t('placeholder.type')"
                        :validation="v$.type"
                        :messages="v$.type.required.$message"/>
                  </div>
                  <BaseInput
                      v-model="formData.colorCode"
                      label="Standortfarbe"
                      :required=true
                      style="width: 100px; float: left"
                      type="color"
                      :inputStyle="{
                               width: '50px',
                               backgroundColor: '#f0f0f0',
                               padding: '1px',
                             }">
                  </BaseInput>
                </div>
                <div class="col-lg-6">
                  <BaseInput
                      v-model="formData.city"
                      label="Stadt"
                      :disabled="formData.hubspotId"
                      :required=true
                      :validation="v$.city"
                      :messages="v$.city.required.$message" />
                  <BaseInput
                      v-model="formData.phone"
                      :disabled="formData.hubspotId"
                      label="Telefonnummer" />
                  <BaseInput
                      v-model="formData.email"
                      label="Email"
                      type="email"
                      :disabled="formData.hubspotId"
                      :validation="v$.email"
                      :messages="v$.email.isValidEmail.$message" />
                  <div v-if="federalStatesDropdown && federalStatesDropdown.length">
                    <SelectedDropdown
                        v-model="formData.federalState"
                        @update:modelValue="updateFederalState"
                        :options="federalStatesDropdown"
                        :required="true"
                        :disabled="formData.hubspotId"
                        :label="$t('general.federal_state')"
                        :placeholder="$t('placeholder.select_federal_state')"
                        :validation="v$.federalState"
                        :messages="v$.federalState.required.$message"/>
                  </div>
                  <BaseInput
                      v-model="formData.regionTranslation"
                      label="Region"
                      :required=true
                      :validation="v$.region"
                      :disabled="formData.hubspotId"
                      :messages="v$.region.required.$message"
                      readonly />
                  <BaseInput
                      v-model="formData.costCenter"
                      label="KS1"
                      :required=true
                      :validation="v$.costCenter"
                      :messages="v$.costCenter.required.$message" />
                </div>
              </div>

              <!-- STATUS / APPROVAL CHECKLIST (BSG only) -->
              <div v-if="isApprovalWorkflowEnabled" class="row mt-3">
                <div class="col-12">
                  <h6 class="mb-3">
                    Status & Freigabe
                    <span v-if="officeId && formData.status"
                          class="badge ms-2 px-2 py-1 align-middle"
                          :class="statusBadgeClass">
                      {{ formData.statusTranslation }}
                    </span>
                  </h6>
                </div>
                <div class="col-lg-6">
                  <small v-if="isApprovalWorkflowEnabled && officeId && formData.status === 'pending'"
                         class="d-block text-muted mb-1">
                    <i class="fa-solid fa-circle-info me-1"></i>Für die Aktivierung muss „Zugelassen am“ eingetragen sein.
                  </small>
                  <Datepicker
                      v-model="formData.approvedOn"
                      label="Zugelassen am"
                      position="up"/>
                  <!-- Closing makes sense only after the office was activated -->
                  <small v-if="officeId && formData.status !== 'pending'"
                         class="d-block text-muted mb-1">
                    <i class="fa-solid fa-circle-info me-1"></i>Datum, ab dem der Standort abgemeldet ist. 7 Tage danach wird er automatisch gelöscht.
                  </small>
                  <Datepicker
                      v-if="officeId && formData.status !== 'pending'"
                      v-model="formData.closedAt"
                      label="Abgemeldet zum"
                      position="up"/>
                  <Datepicker
                      v-model="formData.submittedToCertquaOnDate"
                      label="Eingereicht bei Certqua am"
                      position="up"/>
                </div>
                <div class="col-lg-6">
                  <BaseCheckbox
                      v-model="formData.contractsInPlace"
                      label="Verträge liegen vor"
                      labelPosition="right"/>
                  <BaseCheckbox
                      v-model="formData.floorPlanAvailable"
                      label="Grundriss liegt vor"
                      labelPosition="right"/>
                  <BaseCheckbox
                      v-model="formData.photosAvailable"
                      label="Fotos liegen vor"
                      labelPosition="right"/>
                  <BaseCheckbox
                      v-model="formData.checklistAvailable"
                      label="Checkliste liegt vor"
                      labelPosition="right"/>
                </div>
              </div>

              <div class="form-buttons-w text-center">
                <SaveButton
                    :label="$t('general.button.save')"
                    :loading="isSubmitting"/>
                <!-- Disabled until the required date is entered (backend checks it as well) -->
                <button v-if="isApprovalWorkflowEnabled && officeId && formData.status === 'pending'"
                        type="button"
                        class="status-button status-button-activate ms-2"
                        :disabled="isSubmitting || !formData.approvedOn"
                        :title="!formData.approvedOn ? 'Bitte zuerst „Zugelassen am“ eintragen' : ''"
                        @click="openStatusModal('activate')">
                  <i class="fa-solid fa-circle-check"></i>
                  <span>Speichern und aktivieren</span>
                </button>
                <!-- Shown only once the closing date is entered -->
                <button v-if="isApprovalWorkflowEnabled && officeId && formData.status === 'active' && formData.closedAt"
                        type="button"
                        class="status-button status-button-close ms-2"
                        :disabled="isSubmitting"
                        @click="openStatusModal('close')">
                  <i class="fa-solid fa-circle-xmark"></i>
                  <span>Speichern und abgemeldet zum</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Sidebar></Sidebar>
    </div>

    <ModalConfirm
        v-model="showStatusModal"
        :header="statusAction === 'activate' ? 'Standort aktivieren' : 'Standort abmelden'"
        :confirmationQuestion="statusAction === 'activate'
          ? `Möchten Sie den Standort ${formData.name} speichern und aktivieren?`
          : `Möchten Sie den Standort ${formData.name} speichern und zum ${formData.closedAt} abmelden?`"
        :onConfirm="confirmStatusChange"/>
  </div>
</template>

<script setup lang="ts">
import officeService from '@/api/Office';
import BaseCheckbox from '@/components/base/BaseCheckbox.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import CreateButton from '@/components/CreateButton.vue';
import Sidebar from '@/components/FormSidebar.vue';
import ModalConfirm from '@/components/modal/ModalConfirm.vue';
import Datepicker from '@/components/picker/Datepicker.vue';
import SelectedDropdown from '@/components/SelectDropdown.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {isValidEmail, requiredRule} from '@/shared/validators/validationRules';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import {piniaCompanySettingsStore} from '@/store/piniaCompanySettingsStore';
import useVuelidate from '@vuelidate/core';
import {computed, onMounted, ref, reactive} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import {IOffice, IRegion, IRegionFromFederalState} from "@/interfaces/Office/IOffice";
import BackButton from "@/components/button/BackButton.vue";
import SaveButton from "@/components/button/SaveButton.vue";

const {t} = useI18n();
const piniaEnumsState = piniaEnumsStore();
const getEnums = computed(() => piniaEnumsState.enums);
const federalStatesDropdown = ref([]);
const officeEngagementTypesDropdown = ref([]);
const officeTypesDropdown = ref([]);
const regionsDropdown = ref<IRegion[]>([]);
const isSubmitting = ref(false);
const isLoaded = ref(false);
const showStatusModal = ref(false);
const statusAction = ref<'activate' | 'close' | null>(null);

// Office approval workflow (status, checklist, activate/close) is BSG only, switched on by backend env
const piniaCompanySettingsState = piniaCompanySettingsStore();
const isApprovalWorkflowEnabled = computed(() => !!piniaCompanySettingsState.companySettings?.officeApprovalWorkflowEnabled);
const route = useRoute();
const officeId = route.params.id;

const formData = reactive<IOffice>({
  name: '',
  address: '',
  houseNumber: '',
  zip: '',
  engagementType: '',
  colorCode: '#43ad4e',
  city: '',
  phone: '',
  email: '',
  type: '',
  federalState: '',
  region: '',
  regionTranslation: '',
  costCenter: '',
  approvedOn: null,
  closedAt: null,
  contractsInPlace: false,
  floorPlanAvailable: false,
  photosAvailable: false,
  checklistAvailable: false,
  submittedToCertquaOnDate: null,
});

const statusBadgeClass = computed(() => {
  switch (formData.status) {
    case 'active':
      return 'text-bg-success';
    case 'closed':
      return 'text-bg-danger';
    default:
      return 'text-bg-warning';
  }
});

const rules = computed(() => {
  return {
    name: requiredRule(t),
    address: requiredRule(t),
    houseNumber: requiredRule(t),
    zip: requiredRule(t),
    engagementType: requiredRule(t),
    type: requiredRule(t),
    city: requiredRule(t),
    federalState: requiredRule(t),
    region: requiredRule(t),
    costCenter: requiredRule(t),
    email: isValidEmail(t),
  };
});

const v$ = useVuelidate<IOffice>(rules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (officeId) {
      await officeService.putOffice(formData, +officeId);
    } else {
      await officeService.postOffice(formData);
    }

    await router.push({name: 'officeList'});
    isSubmitting.value = false;
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

function openStatusModal(action: 'activate' | 'close') {
  CloseToast();
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  statusAction.value = action;
  showStatusModal.value = true;
}

// Save first, so the entered approval/closing date is stored before the status check on backend
async function confirmStatusChange() {
  showStatusModal.value = false;

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    await officeService.putOffice(formData, +officeId);

    if (statusAction.value === 'activate') {
      await officeService.activateOffice(+officeId);
    } else if (statusAction.value === 'close') {
      await officeService.closeOffice(+officeId);
    }

    await getOffice();
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
    statusAction.value = null;
  }
}

async function getOffice() {
  try {
    const {data} = await officeService.getOffice(+officeId);
    const regionTranslation = data.data.regionTranslation

    Object.assign(formData, {
      ...data.data,
      regionTranslation: data.data.regionTranslation
    });
  } catch (error) {
    await ErrorToast(error);
  }
}

const updateFederalState = (newValue: string) => {
  // const regionFromFederalState = regionsDropdown.value.find(
  //     regionDropdown => regionDropdown.federal_state_key === newValue
  // );

  const regionFromFederalState = regionsDropdown.value.find(
      regionDropdown => regionDropdown.federal_state_key === newValue
  ) as IRegionFromFederalState | undefined;

  if (regionFromFederalState) {
    formData.region = regionFromFederalState.key;
    formData.regionTranslation = regionFromFederalState.value;
  } else {
    formData.region = '';
    formData.regionTranslation = '';
  }
};

async function getOfficeEnumDropdowns() {
  federalStatesDropdown.value = getEnums.value?.federalStates || [];
  officeTypesDropdown.value = getEnums.value?.officeTypes || [];
  officeEngagementTypesDropdown.value = getEnums.value?.officeEngagementTypes || [];
  regionsDropdown.value = getEnums.value?.regions || [];
}

onMounted(async () => {
  //if (Object.keys(getEnums.value).length === 0) {
  //  await store.dispatch('getEnums', { t });
  //}

  await getOfficeEnumDropdowns();
  if (officeId) {
    await getOffice();
  }

  isLoaded.value = true;
});

</script>

<style scoped>
/* Same size as SaveButton, only colors differ */
.status-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #ffffff;
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.status-button i {
  font-size: 14px;
}

.status-button-activate {
  border: 1px solid #28a745;
  background-color: #28a745;
}

.status-button-close {
  border: 1px solid #dc3545;
  background-color: #dc3545;
}

.status-button:hover:not(:disabled) {
  filter: brightness(0.9);
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08);
}

.status-button:disabled {
  cursor: not-allowed;
  opacity: 0.75;
}
</style>